/**
 * Keeps the audio timeline on the wall clock the video frames are stamped from.
 *
 * Audio and video are each anchored to the wall clock once, at their first frame, and then run on
 * their own clocks. An audio device whose clock runs a fraction of a percent slow (or fast) makes
 * the two drift apart for as long as the stream lasts: the sound lags the picture by half a second
 * per minute at 0.85 %. This corrector watches how far the audio timeline fell behind (or ran
 * ahead of) the wall clock and spreads the correction thinly over every buffer: a few samples
 * inserted (or dropped) by linear interpolation per buffer, never a gap or a click.
 *
 * While the two clocks agree nothing is touched: the output is the input, bit for bit.
 */

/** Smoothing of the error per buffer: arrival jitter of tens of ms is not drift. */
const SMOOTHING = 0.02;
/** Below this the clocks are considered to agree and the audio passes through untouched. */
const DEADBAND_US = 8_000;
/** The error is worked off over about this long. */
const SETTLE_US = 3_000_000;
/** Largest stretch or squeeze of a buffer, as a fraction of its length. */
const MAX_STRETCH = 0.01;

export interface DriftPlan {
  /** Timestamp of the output buffer, µs on the shared clock. */
  tsUs: number;
  /** Frames the output buffer holds: the input's count, plus or minus the correction. */
  outFrames: number;
}

export interface DriftCorrector {
  /**
   * @param frames       frames in the input buffer
   * @param naturalUs    the buffer's timestamp on the shared clock (its own clock, anchored at the first frame)
   * @param wallUs       the wall clock on the same scale, read when the buffer arrived
   * @param canStretch   false when the buffer's format cannot be interpolated: pass through, learn nothing
   */
  plan(frames: number, naturalUs: number, wallUs: number, canStretch?: boolean): DriftPlan;
  /** Output time added so far by the correction, µs (negative: removed). */
  readonly shiftUs: number;
}

export function createDriftCorrector(sampleRate: number): DriftCorrector {
  let shiftUs = 0;
  // Track timestamps can jitter backwards when capture delivers a batch. Each
  // AudioData still contains new samples. Anchor once, then advance by the
  // samples actually emitted so capture jitter cannot discard valid audio.
  let nextSampleUs: number | null = null;
  let baseline: number | null = null;
  let smoothed = 0;
  let carry = 0;

  return {
    get shiftUs() {
      return shiftUs;
    },
    plan(frames, naturalUs, wallUs, canStretch = true) {
      const startUs = nextSampleUs ?? naturalUs;
      const tsUs = Math.round(startUs);
      const emit = (outFrames: number): DriftPlan => {
        if (sampleRate > 0) nextSampleUs = startUs + (outFrames / sampleRate) * 1e6;
        return { tsUs, outFrames };
      };
      if (!canStretch || frames < 2 || !(sampleRate > 0)) return emit(frames);

      const endUs = startUs + (frames / sampleRate) * 1e6;
      const error = wallUs - endUs;
      // The first buffer fixes what "in step" looks like: its own arrival latency.
      if (baseline === null) {
        baseline = error;
        return emit(frames);
      }
      smoothed += SMOOTHING * (error - baseline - smoothed);
      if (Math.abs(smoothed) < DEADBAND_US) {
        carry = 0;
        return emit(frames);
      }

      const fraction = Math.max(-MAX_STRETCH, Math.min(MAX_STRETCH, smoothed / SETTLE_US));
      const want = frames * fraction + carry;
      const whole = Math.trunc(want);
      carry = want - whole;
      const outFrames = Math.max(1, frames + whole);
      shiftUs += ((outFrames - frames) / sampleRate) * 1e6;
      return emit(outFrames);
    },
  };
}

/** Linear interpolation of one plane to `outFrames` samples, endpoints kept. */
export function resamplePlane(input: Float32Array, outFrames: number): Float32Array {
  const n = input.length;
  const out = new Float32Array(outFrames);
  if (n === 0 || outFrames === 0) return out;
  if (n === 1 || outFrames === 1) {
    out.fill(input[0]!);
    return out;
  }
  const step = (n - 1) / (outFrames - 1);
  for (let i = 0; i < outFrames; i++) {
    const pos = i * step;
    const k = Math.min(n - 2, Math.floor(pos));
    const t = pos - k;
    out[i] = input[k]! * (1 - t) + input[k + 1]! * t;
  }
  return out;
}

/** Stretches an `f32-planar` AudioData to `outFrames`, stamped `timestamp`. */
export function stretchAudioData(data: AudioData, timestamp: number, outFrames: number): AudioData {
  const channels = data.numberOfChannels;
  const frames = data.numberOfFrames;
  const buffer = new Float32Array(outFrames * channels);
  for (let c = 0; c < channels; c++) {
    const plane = new Float32Array(frames);
    data.copyTo(plane, { planeIndex: c });
    buffer.set(resamplePlane(plane, outFrames), c * outFrames);
  }
  return new AudioData({
    format: 'f32-planar',
    sampleRate: data.sampleRate,
    numberOfFrames: outFrames,
    numberOfChannels: channels,
    timestamp,
    data: buffer,
  });
}
